/**
 * カレンダーの時間スロット間隔
 * 20分間隔で予約枠を設定する
 */
const calenderSlotInterval = "00:20:00";

/**
 * 予約ビューの表示タイプ（日本語表示名）
 */
enum BookingViewEnum {
  /** 日表示 */
  Day = "日",
  /** 週表示 */
  Week = "週",
  // Month = "月",
}

/**
 * 予約ビューの表示タイプ（英語値）
 */
enum BookingViewTypeEnum {
  /** 日表示 */
  Day = "Day",
  /** 週表示 */
  Week = "Week",
  // Month = "Month",
}

/**
 * 予約ビューのページタイプ
 */
enum BookingViewPageTypeEnum {
  /** リスト表示 */
  List = "List",
  /** 部屋カレンダー表示 */
  RoomCalendar = "RoomCalendar",
  /** スタッフカレンダー表示 */
  StaffCalendar = "StaffCalendar",
}

/**
 * 予約リソースのID
 */
enum BookingResourceId {
  /** 部屋ID */
  Room = "room_id",
  /** スタッフID */
  Staff = "staff_id",
}

/**
 * 予約リソースのフィールド名
 */
enum BookingResourceFieldNameEnum {
  /** 日表示用スタッフID */
  Day = "staffId",
  /** 週表示用スタッフID */
  Week = "staffId",
}

/**
 * 予約ビュータイプ別スロット時間の型定義
 */
type BookingViewTypeSlotDurationEnumType = {
  /** 日表示のスロット時間 */
  Day: string;
  /** 週表示のスロット時間 */
  Week: string;
};

/**
 * 予約ビュータイプ別スロット時間の設定
 */
const BookingViewTypeSlotDurationEnum: BookingViewTypeSlotDurationEnumType = {
  Day: calenderSlotInterval,
  Week: "24:00",
};

/**
 * 予約オプションの型定義
 */
type IBookingOption = {
  /** 表示名 */
  name: string;
  /** 値 */
  value: string;
};

/**
 * 予約カラーオプションの型定義
 */
type IBookingColorOption = {
  /** 表示名 */
  name: string;
  /** 値 */
  value: string;
  /** CSSカラークラス */
  colorClass: string;
};

/**
 * 予約ビューオプションの配列
 * BookingViewEnumから生成される
 */
const BookingViewOptions: IBookingOption[] = Object.entries(BookingViewEnum).map(([value, name]) => ({
  name,
  value,
}));

/**
 * すべての予約ステータスを表す定数
 */
const BookingStatusFull = "すべて";

/**
 * 予約ステータスの列挙型
 */
enum BookingStatusEnum {
  /** 未来店（予約済み） */
  will_come = "未来店",
  /** 受付済み */
  booked = "受付済",
  /** 会計済み */
  completed = "会計済",
  /** 来店済み */
  visited = "来店済",
  /** 任意キャンセル */
  cancel = "任意キャンセル",
  /** 無断キャンセル */
  cancel_not_notice = "無断キャンセル",
}

/**
 * 全予約ステータスの列挙型（すべてを含む）
 */
enum FullBookingStatusEnum {
  /** すべて */
  full = "すべて",
  /** 未来店（予約済み） */
  will_come = "未来店",
  /** 来店済み */
  visited = "来店済",
  /** 任意キャンセル */
  cancel = "任意キャンセル",
  /** 無断キャンセル */
  cancel_not_notice = "無断キャンセル",
}

/**
 * 配信グループ予約ステータスの列挙型
 */
enum DistributionGroupBookingStatusEnum {
  /** すべて */
  full = "すべて",
  /** 未来店（予約済み） */
  will_come = "未来店",
  /** 来店済み */
  visited = "来店済",
  /** キャンセル */
  cancel = "任意キャンセル",
  /** 無断キャンセル */
  cancel_not_notice = "無断キャンセル",
}

/**
 * 予約ステータス × 顧客ステータス別のCSSカラークラス
 *
 * キー形式:
 * - 予約（will_come）: `will_come_{customer_status}` (new, existed, revisited)
 * - その他: 既存のキー名を維持
 */
const BookingStatusColorClass = {
  // 予約 × 顧客ステータス
  will_come_new: "bg-booking-new",
  will_come_existed: "bg-booking-existed",
  will_come_revisited: "bg-booking-revisited",

  // その他のステータス（予約カレンダー凡例用: 新仕様カラー）
  cancel: "bg-calendar-cancel",
  cancel_not_notice: "bg-calendar-cancel-not-notice",
  visited: "bg-calendar-visited",
  completed: "bg-calendar-visited",
  booked: "bg-calendar-visited", // 受付済みも来店済みと同じ色

  // 後方互換性のため保持（非推奨）
  will_come: "bg-booking-existed", // デフォルトは既存顧客色
  booking_request: "bg-booking-existed", // リクエスト予約は既存顧客と同じ色
  completed_visited: "bg-calendar-visited",
};

/**
 * 予約ソース別のCSSカラークラス
 * @deprecated 顧客ステータスで管理するため廃止予定
 */
const BookingSourceColorClasses = {
  // hotpepper: "bg-hotpepper",  // 旧仕様: 顧客ステータスで管理するため廃止
};

/**
 * 予約の第二ステータス（凡例表示用）
 */
const BookingSecondStatus = {
  will_come_new: "予約・新規",
  will_come_existed: "予約・既存",
  will_come_revisited: "予約・再来",
  cancel: "任意キャンセル",
  cancel_not_notice: "無断キャンセル",
  visited: "来店済",
};

/**
 * 予約ステータス検索用の列挙型
 */
enum BookingStatusSearchEnum {
  /** デフォルト選択肢 */
  default = "-- 選ぶ --",
  /** 未来店（予約済み） */
  will_come = "未来店",
  /** 来店済み */
  visited = "来店済",
  /** 任意キャンセル */
  cancel = "任意キャンセル",
  /** 無断キャンセル */
  cancel_not_notice = "無断キャンセル",
}

/**
 * 予約メニュー検索用の列挙型
 */
enum BookingMenuSearchEnum {
  /** ID */
  id = 0,
  /** デフォルト選択肢 */
  name = "-- 選ぶ --",
}

/**
 * 非アクティブな予約ステータスの配列
 * これらのステータスは表示オプションから除外される
 */
const inactiveBookingStatus = ["booked", "completed"];

/**
 * 予約ステータスオプションの配列
 * 非アクティブなステータスを除外して生成される
 */
const BookingStatusOptions: IBookingOption[] = Object.entries(BookingStatusEnum)
  .filter(([key]) => !inactiveBookingStatus.includes(key))
  .map(([value, name]) => ({
    name,
    value,
  }));

/**
 * 全予約ステータスオプションの配列
 */
const FullBookingStatusOptions: IBookingOption[] = Object.entries(FullBookingStatusEnum).map(([value, name]) => ({
  name,
  value,
}));

/**
 * 配信グループ予約ステータスオプションの配列
 */
const DistributionGroupBookingStatusOptions: IBookingOption[] = Object.entries(DistributionGroupBookingStatusEnum).map(
  ([value, name]) => ({
    name,
    value,
  })
);

/**
 * 予約ステータスカラーオプションの配列
 * カラークラス情報を含む
 */
const BookingStatusColorOptions: IBookingColorOption[] = Object.entries(BookingSecondStatus).map(([value, name]) => ({
  name,
  value,
  colorClass: BookingStatusColorClass[value as keyof typeof BookingStatusColorClass],
}));

/**
 * 予約ステータス検索オプションの配列
 */
const BookingStatusSearchOptions: IBookingOption[] = Object.entries(BookingStatusSearchEnum).map(([value, name]) => ({
  name,
  value,
}));

/**
 * 予約時間オプションの配列（00:00から23:00まで）
 */
const BookingTimeOptions: IBookingOption[] = Array.from({ length: 24 }, (_, i) => {
  const time = `${i.toString().padStart(2, "0")}:00`;
  return { name: time, value: time };
});

/**
 * 予約時間オプションの配列（0時から23時まで）
 */
const BookingHourOptions: IBookingOption[] = Array.from({ length: 24 }, (_, i) => {
  const time = `${i.toString().padStart(2, "0")}`;
  return { name: time + "時", value: time };
});

/**
 * 予約分オプションの配列（0分から55分まで、5分間隔）
 */
const BookingMinuteOptions: IBookingOption[] = Array.from({ length: 12 }, (_, i) => {
  const time = `${(i * 5).toString().padStart(2, "0")}`;
  return { name: time + "分", value: time };
});

/**
 * 予約顧客ステータスの列挙型
 */
enum BookingCustomerStatus {
  /** 新規顧客 */
  new = "新規",
  /** 既存顧客 */
  existed = "既存",
}

/**
 * 予約アクションの列挙型
 */
enum BookingActionEnum {
  /** 新規予約 */
  new = "NewBooking",
  /** 予約表示 */
  show = "ShowBooking",
  /** 予約編集 */
  edit = "EditBooking",
  /** 予約複製 */
  duplicate = "DuplicateBooking",
}

/**
 * 予約ビュータイプ別日数の列挙型
 */
enum BookingViewTypeDaysEnum {
  /** 日表示の日数 */
  day = 1,
  /** 週表示の日数 */
  week = 7,
}

export {
  BookingViewEnum,
  BookingViewTypeEnum,
  BookingResourceFieldNameEnum,
  BookingResourceId,
  BookingViewTypeDaysEnum,
  BookingViewTypeSlotDurationEnum,
  BookingViewOptions,
  BookingStatusFull,
  BookingStatusEnum,
  BookingStatusOptions,
  BookingStatusColorOptions,
  BookingTimeOptions,
  BookingHourOptions,
  BookingMinuteOptions,
  BookingCustomerStatus,
  BookingActionEnum,
  BookingStatusColorClass,
  BookingSourceColorClasses,
  BookingViewPageTypeEnum,
  BookingStatusSearchOptions,
  FullBookingStatusOptions,
  BookingMenuSearchEnum,
  DistributionGroupBookingStatusOptions,
};
